/**
 * AOIPanel Component
 *
 * Right panel displaying live drone feed, mission details, and flight controls
 */

import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { CircleX, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCreateAOIMutation, useUpdateAOIMutation, useAOIQuery } from '@/features/aoi/api/aoi.api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { HexColorPicker } from 'react-colorful';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

// type HexColor = `#${string}`;

interface AOIPanelProps {
    className?: string;
    aoiId?: string;
}
interface AOIFormData {
    aoiName: string;
    description: string;
}

export interface Point {
    id: number;
    x: string;
    y: string;
}

interface AOIPanelProps {
    className?: string;
    aoiId?: string;
    points: Point[];
    setPoints: React.Dispatch<React.SetStateAction<Point[]>>;
}

export function AOIPanel({ className, aoiId, points, setPoints }: AOIPanelProps) {
    // Fetch AOI data if in update mode
    const { data: aoiData, isLoading } = useAOIQuery(aoiId || null);
    // const [flightTime, setFlightTime] = useState(23 * 60 + 45); // 23:45 in seconds
    // const [totalFlightTime] = useState(35 * 60 + 8); // 35:08 in seconds
    // const [isLive, setIsLive] = useState(true);
    // const [points, setPoints] = useState<Point[]>([]); removed local state
    const isSelectingPoints = useState(false);
    const [selectedColor, setSelectedColor] = useState<string>('#1C5CAA');
    const [showColorPicker, setShowColorPicker] = useState(false);
    const colorPickerRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const isUpdateMode = !!aoiId;
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleRemoveAllPoints = () => {
        setPoints([]);
    };

    const handleRemovePoint = (id: number) => {
        setPoints(points.filter(point => point.id !== id));
    };

    const { register, handleSubmit, reset, formState: { errors } } = useForm<AOIFormData>({
        defaultValues: {
            aoiName: aoiData?.aoiName || '',
            description: aoiData?.description || ''
        }
    });

    // Update form when AOI data is loaded
    useEffect(() => {
        if (aoiData) {
            reset({
                aoiName: aoiData.aoiName,
                description: aoiData.description || ''
            });
        }
    }, [aoiData, reset]);

    const createMutation = useCreateAOIMutation();
    const updateMutation = useUpdateAOIMutation();

    const onSubmit = async (data: AOIFormData) => {
        try {
            setIsSubmitting(true);
            if (isUpdateMode && aoiId) {
                await updateMutation.mutateAsync({
                    id: aoiId,
                    data,
                });
                toast.success('Area of Interest updated successfully');
            } else {
                await createMutation.mutateAsync(data);
                toast.success('Area of Interest created successfully');
                reset();
            }
            navigate('/mission-planning/area-of-interest');
        } catch (error) {
            toast.error(isUpdateMode ? 'Failed to update AOI' : 'Failed to create AOI');
            console.error('Form submission error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // useEffect(() => {
    //     if (!isLive) return;

    //     const interval = setInterval(() => {
    //         setFlightTime((prev) => {
    //             if (prev >= totalFlightTime) {
    //                 setIsLive(false);
    //                 return totalFlightTime;
    //             }
    //             return prev + 1;
    //         });
    //     }, 1000);

    //     return () => clearInterval(interval);
    // }, [isLive, totalFlightTime]);


    // Close color picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
                setShowColorPicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    return (
        <div
            className={cn(
                'bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-lg shadow-2xl',
                'flex flex-col h-full overflow-hidden flex-1',
                className
            )}
        >
            {/* Header */}
            <div className="px-3 py-2 border-slate-700 flex-shrink-0">
                <h2 className="text-sm font-semibold text-white">Area of Interest</h2>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="px-3 py-3 space-y-4 flex-shrink-0 rounded-md flex-1 overflow-y-auto">
                <div className="bg-slate-700 px-3 py-2 rounded-md space-y-2">
                    {/* Name Field */}
                    <div className="space-y-2">
                        <Label htmlFor="aoiName" className="text-xs">Name *</Label>
                        <Input
                            id="aoiName"
                            type="text"
                            placeholder="Enter AOI name"
                            {...register('aoiName', {
                                required: 'Name is required',
                                minLength: { value: 3, message: 'Name must be at least 3 characters' },
                            })}
                            className="w-full bg-slate-800 text-white text-sm"
                        />
                        {errors.aoiName && (
                            <p className="text-xs text-red-400 mt-1">{errors.aoiName.message}</p>
                        )}
                    </div>

                    {/* Description Field */}
                    <div className="space-y-2">
                        <Label htmlFor="description" className="text-xs">Description</Label>
                        <Textarea
                            id="description"
                            placeholder="Enter AOI description (optional)"
                            {...register('description')}
                            rows={3}
                            className="w-full bg-slate-800 text-white text-sm resize-none"
                        />
                        {errors.description && (
                            <p className="text-xs text-red-400 mt-1">{errors.description.message}</p>
                        )}
                    </div>
                </div>

                <div className="flex-shrink-0 rounded-md relative" ref={colorPickerRef}>
                    <div className="bg-slate-700 px-3 py-3 rounded-md space-y-2">
                        <div className="flex justify-between items-center">
                            <label className="text-xs text-white">Points</label>
                            {/* <Button 
                                type="button"
                                variant={isSelectingPoints ? 'default' : 'outline'}
                                size="sm"
                                className="text-xs flex items-center gap-1"
                                // onClick={handleTogglePointSelection}
                            >
                                <MapPin size={14} />
                                {isSelectingPoints ? 'Stop Adding Points' : 'Add Points'}
                            </Button> */}
                        </div>
                        <div className="flex flex-row mb-4 gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowColorPicker(!showColorPicker)}
                                className="w-[85%] bg-blue-700 text-white hover:bg-blue-600/90 hover:text-gray-200"
                                size="sm"
                            >
                                Select Color
                            </Button>
                            <div
                                className="w-[15%] border border-slate-600 rounded cursor-pointer"
                                style={{ backgroundColor: selectedColor }}
                                onClick={() => setShowColorPicker(!showColorPicker)}
                            />
                        </div>
                        {showColorPicker && (
                            <div className="absolute z-10 mt-1">
                                <div className="p-2 bg-white rounded shadow-lg">
                                    <HexColorPicker
                                        color={selectedColor}
                                        onChange={setSelectedColor}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>


                {/* Flight Time Progress */}
                <div className="flex-shrink-0 ">
                    <div className="bg-slate-700 px-3 py-2 rounded-md">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-white">Points</span>
                            {points.length > 0 && (
                                <button
                                    type="button"
                                    onClick={handleRemoveAllPoints}
                                    className="text-sm text-white bg-blue-600 hover:bg-blue-700 p-2 rounded font-medium hover:text-red-300 transition-colors"
                                >
                                    Remove all Points
                                </button>
                            )}
                        </div>
                        {points.length === 0 ? (
                            <div className="flex items-center justify-center py-8">
                                <p className="text-slate-400">No Data</p>
                            </div>
                        ) : (
                            points.length > 0 ? (
                                points.map((point, index) => (
                                    <div key={point.id}>
                                        <label className="text-xs text-white block mb-1">Point {index + 1}</label>
                                        <div className='flex flex-row gap-4 w-full'>
                                            <div className="bg-slate-800 p-2 rounded-md mb-4 w-[40%]">
                                                <p className="text-sm text-white font-mono">{point.y}</p>
                                            </div>
                                            <div className="bg-slate-800 p-2 rounded-md mb-4 w-[40%]">
                                                <p className="text-sm text-white font-mono">{point.x}</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemovePoint(point.id)}
                                                className="p-2 rounded-md mb-4 text-slate-400 hover:text-white transition-colors"
                                            >
                                                <CircleX size={20} />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-4 text-slate-400 text-sm">
                                    {isSelectingPoints ?
                                        'Click on the map to add points' :
                                        'No points added. Click "Add Points" to start.'}
                                </div>
                            )
                        )}
                        {/* </div> */}
                    </div>
                </div>
            </form>

            {/* Submit Button */}
            <div className="mt-auto px-3 py-3 flex-shrink-0 flex gap-2">
                <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={isSubmitting}
                    onClick={handleSubmit(onSubmit)}
                >
                    {isSubmitting ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {isUpdateMode ? 'Updating...' : 'Creating...'}
                        </>
                    ) : (
                        isUpdateMode ? 'Update AOI' : 'Create AOI'
                    )}
                </Button>
            </div>
        </div>
    );
}
